import { useState } from 'react';
import { ChevronDown, ChevronUp, Mail, MapPin, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { HeaderLanding } from "../components/ui/header-landings";
import { Footer } from '../components/ui/footer';
import { toast } from 'sonner';

const faqs = [
  {
    question: "¿Cómo puedo participar en un sorteo?",
    answer: "Para participar, selecciona el sorteo de tu interés, elige los números que deseas y completa el formulario con tus datos. Una vez apartados los boletos, realiza el pago según las instrucciones y envía tu comprobante por WhatsApp."
  },
  {
    question: "¿Còmo se elige a los ganadores?",
    answer: "Todos nuestros sorteos se realizan con base en los resultados oficiales de la Lotería Nacional para la Asistencia Pública y SorteosTec Efectivo. El ganador serà el participante que tenga el boleto con los números que coincidan con los resultados oficiales al momento del cierre del sorteo."
  },
  {
    question: "¿Qué pasa si el boleto ganador no fue vendido?",
    answer: "¡Tu boleto vale por dos! Si el premio no encuentra dueño, realizaremos un nuevo sorteo en una fecha cercana. ¡Mantén tu boleto a la mano porque tendrás una segunda oportunidad de ganar!"
  },
  {
    question: "¿Cuáles son los métodos de pago aceptados?",
    answer: "Aceptamos transferencias bancarias, depósitos en OXXO y pagos por SPEI. Puedes ver todos los detalles en nuestra página de Métodos de Pago."
  },
  {
    question: "¿Cuánto tiempo tengo para pagar mis boletos apartados?",
    answer: "Tienes 24 horas para realizar el pago de tus boletos apartados. Después de este tiempo, los boletos podrían ser liberados y quedar disponibles para otros participantes."
  },
  {
    question: "¿Cómo sé si mi pago fue confirmado?",
    answer: "Una vez que envíes tu comprobante de pago por WhatsApp o por correo electrónico, nuestro equipo verificará la transacción y confirmará tu compra. Puedes consultar el estado de tus boletos en la sección 'Verificador de boletos'."
  },
  {
    question: "¿Qué pasa si no gano el sorteo?",
    answer: "Si no resultas ganador, te invitamos a participar en nuestros próximos sorteos. Recuerda que cada boleto es una oportunidad de ganar increíbles premios."
  },
  {
    question: "¿Puedo cancelar mi compra y obtener un reembolso?",
    answer: "Las compras de boletos son finales y no se realizan reembolsos una vez confirmado el pago. Te recomendamos estar seguro antes de completar tu compra."
  },
  {
    question: "¿Cómo me entero de los resultados del sorteo?",
    answer: "Los resultados se publican en nuestra página principal y se notifica al ganador directamente por teléfono y WhatsApp. También puedes seguirnos en redes sociales donde se anuncian los resultados de cada sorteo."
  },
  {
    question: "¿Qué sucede con el sorteo si no se alcanza el mínimo de boletos vendidos?",
    answer: "Si no se alcanza el mínimo de boletos vendidos para un sorteo, se realizará un nuevo sorteo en una fecha cercana. Todos los boletos apartados para ese sorteo seguirán siendo válidos para el nuevo sorteo, por lo que no es necesario realizar ningún cambio o reembolso."
  }
];

export default function ContactPage() {
  const [openFaq, setOpenFaq] = useState(null);

  const [form, setForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Mensaje enviado. Te contactaremos pronto.');
    setForm({ name: '', email: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <HeaderLanding />

      <main>
        {/* Hero Section */}
        <section className="border-b-2 border-black py-16 bg-[#F3F4F6]">
          <div className="px-4 sm:px-8 max-w-7xl mx-auto text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3333] mb-2">
              Estamos para ayudarte
            </p>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4">
              CONTACTO
            </h1>
            <p className="text-[#4B5563] max-w-md mx-auto">
              ¿Tienes dudas? Consulta nuestras preguntas frecuentes o envíanos un mensaje.
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 border-b-2 border-black">
          <div className="px-4 sm:px-8 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3333] mb-2">
                Resuelve tus dudas
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
                PREGUNTAS FRECUENTES
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="border-2 border-black bg-white shadow-[4px_4px_0_0_#000000]"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-6 text-left"
                    data-testid={`faq-question-${index}`}
                  >
                    <span className="font-bold text-lg pr-4">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-6 h-6 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-6 h-6 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6 text-[#4B5563] leading-relaxed border-t-2 border-black pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Info + Form Section */}
        <section className="py-16">
          <div className="px-4 sm:px-8 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3333] mb-2">
                  Información de contacto
                </p>
                <h2 className="text-3xl font-bold tracking-tight mb-8">
                  CONTÁCTANOS
                </h2>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0A0A0A] flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">WhatsApp</h3>
                      <p className="text-[#4B5563]">+52 55 6472 3223</p>
                      <p className="text-sm text-[#4B5563]">Lun - Vie: 9:00 AM - 6:00 PM</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0A0A0A] flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">Email</h3>
                      <p className="text-[#4B5563]">zeitex_mx@outlook.com</p>
                      <p className="text-sm text-[#4B5563]">Respuesta en 24-48 horas</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0A0A0A] flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">Ubicación</h3>
                      <p className="text-[#4B5563]">Ciudad de México, México</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-[#F3F4F6] border-2 border-black p-8 shadow-[8px_8px_0_0_#000000]">
                <h3 className="text-2xl font-bold mb-6">Envíanos un mensaje</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-bold">Nombre</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      className="border-2 border-black rounded-none focus:ring-0 focus:border-[#FF3333]"
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-bold">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      className="border-2 border-black rounded-none focus:ring-0 focus:border-[#FF3333]"
                      placeholder="tu@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-bold">Mensaje</Label>
                    <textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      required
                      rows={4}
                      className="w-full border-2 border-black rounded-none px-4 py-3 focus:ring-0 focus:border-[#FF3333] focus:outline-none"
                      placeholder="¿En qué podemos ayudarte?"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-[#FF3333] text-white font-bold uppercase tracking-wider border-2 border-black px-6 py-3 shadow-[4px_4px_0_0_#000000] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_0_#000000] transition-all"
                  >
                    Enviar Mensaje
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

       {/* Footer */}
          <Footer />
    </div>
  );
}
